import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Flex, Heading, Input, Table, Thead, Tbody, Tr, Th, Td,
  Tag, Switch, useColorModeValue, useToast, IconButton, Select, Spinner
} from "@chakra-ui/react";
import { MdRefresh } from "react-icons/md";
import { listUsers, toggleActive, updateRole, removeUser } from "services/users";

export default function UserManagement() {
  const toast = useToast();
  const cardBg = useColorModeValue("white", "navy.800");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");

  // 상태
  const [qInput, setQInput] = useState(""); // 입력 UI
  const [q, setQ] = useState("");           // 실제 쿼리
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // 검색 디바운스
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // 목록 불러오기
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { items, total } = await listUsers({ q, page, page_size: pageSize });
        setRows(items || []);
        setTotal(total || 0);
      } catch (e) {
        const status = e?.response?.status;
        if (![401, 403].includes(status)) {
          toast({
            status: "error",
            title: "사용자 목록 실패",
            description: String(e?.response?.data?.detail || e?.message),
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [q, page, pageSize, refreshKey, toast]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / pageSize)), [total, pageSize]);

  // 핸들러
  const onToggleActive = async (user) => {
    const next = !user.is_active;
    setRows(prev => prev.map(x => x.id === user.id ? { ...x, is_active: next } : x)); // 낙관적
    try {
      await toggleActive(user.id, next);
      toast({ status: "success", title: next ? "활성화" : "비활성화" });
    } catch (e) {
      setRows(prev => prev.map(x => x.id === user.id ? { ...x, is_active: !next } : x)); // 롤백
      toast({ status: "error", title: "상태 변경 실패", description: String(e?.response?.data?.detail || e?.message) });
    }
  };

  const onChangeRole = async (user, role) => {
    const old = user.role;
    setRows(prev => prev.map(x => x.id === user.id ? { ...x, role } : x)); // 낙관적
    try {
      await updateRole(user.id, role);
      toast({ status: "success", title: "권한 변경 완료" });
    } catch (e) {
      setRows(prev => prev.map(x => x.id === user.id ? { ...x, role: old } : x)); // 롤백
      toast({ status: "error", title: "권한 변경 실패", description: String(e?.response?.data?.detail || e?.message) });
    }
  };

  const onDelete = async (user) => {
    if (!window.confirm(`삭제하시겠습니까? (${user.email})`)) return;
    const prev = rows;
    setRows(prev.filter(x => x.id !== user.id)); // 낙관적
    setTotal(t => Math.max(0, t - 1));
    try {
      await removeUser(user.id);
      toast({ status: "success", title: "삭제 완료" });
    } catch (e) {
      setRows(prev); // 롤백
      setTotal(t => t + 1);
      toast({ status: "error", title: "삭제 실패", description: String(e?.response?.data?.detail || e?.message) });
    }
  };

  return (
    <Box pt={{ base: "110px", md: "80px" }}>
      <Box bg={cardBg} border="1px solid" borderColor={border} rounded="2xl" p="20px">
        <Flex justify="space-between" align="center" mb="12px" gap="10px" wrap="wrap">
          <Heading size="md">사용자 관리</Heading>
          <Flex gap="8px">
            <IconButton icon={<MdRefresh />} aria-label="refresh" onClick={() => setRefreshKey(k => k + 1)} />
          </Flex>
        </Flex>

        <Flex gap="10px" mb="12px" wrap="wrap">
          <Input
            placeholder="이름/이메일 검색"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
          <Button onClick={() => { setQInput(""); setQ(""); setPage(1); }}>
            초기화
          </Button>
        </Flex>

        <Box overflow="auto" border="1px solid" borderColor={border} rounded="xl" minH="220px">
          {loading ? (
            <Flex align="center" justify="center" h="220px"><Spinner /></Flex>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>이름</Th>
                  <Th>이메일</Th>
                  <Th>권한</Th>
                  <Th>활성</Th>
                  <Th>액션</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr key={r.id}>
                    <Td>{r.id}</Td>
                    <Td>{r.name}</Td>
                    <Td>{r.email}</Td>
                    <Td>
                      <Tag colorScheme={r.role === "admin" ? "purple" : "gray"} mr="8px">
                        {r.role}
                      </Tag>
                      <Select
                        size="xs"
                        w="140px"
                        mt="6px"
                        value={r.role}
                        onChange={(e) => onChangeRole(r, e.target.value)}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </Select>
                    </Td>
                    <Td>
                      <Switch isChecked={r.is_active} onChange={() => onToggleActive(r)} />
                    </Td>
                    <Td>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => onDelete(r)}>
                        삭제
                      </Button>
                    </Td>
                  </Tr>
                ))}
                {!rows.length && !loading && (
                  <Tr>
                    <Td colSpan={6}>
                      <Box p="16px" textAlign="center" color="gray.500">데이터가 없습니다.</Box>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* 페이징 */}
        <Flex justify="space-between" align="center" mt="12px" wrap="wrap" gap="10px">
          <Box fontSize="sm" color="gray.500">총 {total}명</Box>
          <Flex gap="8px">
            <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page === 1}>이전</Button>
            <Box fontSize="sm" minW="80px" textAlign="center">
              {page} / {totalPages}
            </Box>
            <Button size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} isDisabled={page >= totalPages}>다음</Button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
